import { DisplayMode, ViewLink } from "@ppb/tbd-store/state/layout/views/ViewLink.types";
import { NavigationIntent } from "@ppb/tbd-store/clients/catalogue/catalogue-response-types";
import { ViewLink as TheWallViewLink } from "@ppb/the-wall-common/types";
import { DisplayMode as TheWallDisplayMode } from "@ppb/the-wall-common/types/ViewLink.types";

/**
 * the-wall's `ViewLink` has no `navigationIntent` and its own `DisplayMode` enum
 * (same string values as tbd-store's, but a nominally distinct type). Converts
 * the canonical tbd-store `ViewLink` into the shape the-wall components expect,
 * dropping `navigationIntent` since the-wall has no use for it.
 */
export function toTheWallViewLink(viewLink: ViewLink): TheWallViewLink {
  const { viewUrn, viewUrl, viewDisplayMode, gtmData } = viewLink;

  return {
    viewUrn,
    viewUrl,
    viewDisplayMode: viewDisplayMode as unknown as TheWallDisplayMode | null | undefined,
    gtmData,
  };
}

/**
 * the-wall callbacks (onPress/onTap handlers) hand back their own `ViewLink`
 * shape, which has no `navigationIntent`. These callbacks only ever fire for
 * in-app selection actions (race switchers, tabs, bottom bar, category links),
 * so `navigationIntent` defaults to Internal unless the caller knows better.
 */
export function fromTheWallViewLink(
  viewLink: TheWallViewLink,
  navigationIntent: NavigationIntent = NavigationIntent.Internal,
): ViewLink {
  const { viewUrn, viewUrl, viewDisplayMode, gtmData } = viewLink;

  return {
    viewUrn,
    viewUrl,
    viewDisplayMode: viewDisplayMode as unknown as DisplayMode | null | undefined,
    gtmData,
    navigationIntent,
  };
}

/**
 * tbd-components' `ViewLink` shapes carry `navigationIntent` as their own
 * `'EXTERNAL' | 'INTERNAL'` union and their own `DisplayMode` enum — both
 * nominally distinct from tbd-store's equivalents though the string values
 * are identical. Converts a component viewLink into the canonical tbd-store
 * `ViewLink`, mapping navigationIntent onto the enum.
 */
export function fromTbdComponentsViewLink(viewLink: {
  viewUrn: string;
  viewUrl: string;
  viewDisplayMode?: unknown;
  navigationIntent?: "EXTERNAL" | "INTERNAL";
}): ViewLink {
  const { viewUrn, viewUrl, viewDisplayMode, navigationIntent } = viewLink;

  return {
    viewUrn,
    viewUrl,
    viewDisplayMode: viewDisplayMode as unknown as DisplayMode | null | undefined,
    navigationIntent:
      navigationIntent === "EXTERNAL"
        ? NavigationIntent.External
        : navigationIntent === "INTERNAL"
        ? NavigationIntent.Internal
        : undefined,
  };
}
